import produce from 'immer';

import {
  IBooleanCondition,
  IBooleanExpressionCondition,
} from '../../inputs/expressions-editor/conditions/IBooleanCondition';
import { ReferencePathDirection } from '../../schema/expressions/references/IReferencePath';
import { collectExpressionReferences } from '../../schema/expressions/references/collectExpressionReferences';
import { findIndexReferenceChildOf } from '../../schema/expressions/references/findIndexReferenceChildOf';
import { ICodeWidget } from '../../schema/widgets/ICodeWidget';
import { IParentWidgetScope } from '../../schema/widgets/IWidgetType';

/**
 * In caso di widget annidati, è necessario che le referenze all'interno delle
 * condizioni di visibilità e abilitazione siano riferite al widget padre;
 * per questo motivo, è necessario "rimappare" le reference, in modo tale
 * che ad esempio, da `flag` diventino `^.flag`.
 */
export function remapReferencesUpwardInScope(
  parent: ICodeWidget,
  parentScope: IParentWidgetScope,
): IParentWidgetScope {
  return parentScope.map(scope => {
    return {
      ...scope,
      visible:
        scope.visible?.type === 'expression'
          ? remapExpressionReferencesUpwardInScope(parent, scope.visible)
          : scope.visible,
      enabled:
        scope.enabled?.type === 'expression'
          ? remapExpressionReferencesUpwardInScope(parent, scope.enabled)
          : scope.enabled,
    };
  });
}

export function remapExpressionReferencesUpwardInScope(
  parent: ICodeWidget,
  condition: IBooleanExpressionCondition,
) {
  return produce(condition, draft => {
    collectExpressionReferences(draft.expression).forEach(reference => {
      // Se il padre fornito è già nel `path` della reference, ma come `child`,
      // vuol dire che questa Reference puntava _in basso_
      // ^ (verso il parent) + v (verso il child) si elidono, quindi il risultato
      // è che eliminiamo il path `Child` (ci troviamo allo stesso livello)
      const childIndex = findIndexReferenceChildOf(reference, parent.code);
      if (childIndex >= 0) {
        reference.path.splice(childIndex, 1);
        return;
      }

      reference.path.unshift({
        direction: ReferencePathDirection.Parent,
        code: parent.code,
      });
    });
  });
}

export function remapBooleanConditionReferencesUpwardInScope(
  parent: ICodeWidget,
  condition: IBooleanCondition | null | undefined,
) {
  if (!condition || condition.type === 'static') return condition;
  return remapExpressionReferencesUpwardInScope(parent, condition);
}
